import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SignInHeader from "../auth/SigninHeader";
import Navigation from "../layout/Navigation";

const InspectionType = () => {
  const navigate = useNavigate();

  const [authenticated, setAuthenticated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [group, setGroup] = useState("");
  const [type, setType] = useState("");
  const [subtype, setSubType] = useState("");
  const [category, setCategory] = useState("");
  const [user, setUser] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const [g6ActionTypes, setG6ActionTypes] = useState([]);
  const [g6Loading, setG6Loading] = useState(false);
  const [g6Error, setG6Error] = useState("");
  const [selectedG6Type, setSelectedG6Type] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    const savedTheme = localStorage.getItem("theme");
    const prefersDark =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    
    setIsDarkMode(savedTheme === "dark" || (!savedTheme && prefersDark));
    
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing stored user:', e);
        localStorage.removeItem('user');
      }
    }

    setSelectedTemplate(localStorage.getItem("selectedTemplate") || "AA - Received");
    setGroup(localStorage.getItem("group") || "Building");
    setType(localStorage.getItem("type") || "Residential");
    setSubType(localStorage.getItem("subtype") || "NA");
    setCategory(localStorage.getItem("category") || "NA");
    setSelectedG6Type(localStorage.getItem("selectedG6Type") || null);

    // Check authentication status
    const isAuthenticated = !!token;
    setAuthenticated(isAuthenticated);

    if (!isAuthenticated) {
      setErrorMessage("Please sign in to access this page.");
      navigate("/signin");
      return;
    }

    // Fetch G6 action types
    handleGetG6ActionTypes();
  }, [navigate]);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem("theme", newMode ? "dark" : "light");
  };

  const handleGetG6ActionTypes = async () => {
    setG6Loading(true);
    setG6Error("");
    setG6ActionTypes([]);
    setSelectedG6Type(null);
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setG6Error("Authentication token missing. Please sign in.");
        navigate("/signin");
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get-g6-action-types`, {
        headers: { Authorization: token },
      });

      console.log('G6 Action Types response:', response.data);

      if (response.data.success) {
        const g6Types = response.data.g6ActionTypes;
        
        console.log('Raw g6Types:', g6Types); // Debug log
        
        if (g6Types && typeof g6Types === 'string') {
          if (g6Types.includes("No G6 Action Types found")) {
            setG6ActionTypes([]);
            setG6Error("No G6 Action Types found for the selected record type.");
          } else {
            // More robust parsing that handles different line endings and whitespace
            const parsedTypes = g6Types
              .split(/\r?\n/) // Handle both \n and \r\n line endings
              .filter(line => line.trim().startsWith('- ')) // Filter lines that start with '- '
              .map(line => line.replace(/^-\s*/, '').trim()); // Remove '- ' prefix and trim
            
            console.log('Parsed types:', parsedTypes); // Debug log
            console.log('Parsed types length:', parsedTypes.length); // Debug log
            
            if (parsedTypes.length > 0) {
              setG6ActionTypes(parsedTypes);
              
              // Check if stored G6 type is valid
              const storedG6Type = localStorage.getItem("selectedG6Type");
              if (storedG6Type && parsedTypes.includes(storedG6Type)) {
                setSelectedG6Type(storedG6Type);
              }
            } else {
              // If no types found after parsing, show error
              setG6ActionTypes([]);
              setG6Error("No valid G6 Action Types found in the response.");
            }
          }
        } else if (Array.isArray(g6Types)) {
          // Handle array format
          setG6ActionTypes(g6Types);
          const storedG6Type = localStorage.getItem("selectedG6Type");
          if (storedG6Type && g6Types.includes(storedG6Type)) {
            setSelectedG6Type(storedG6Type);
          }
        } else if (typeof g6Types === 'object' && g6Types !== null) {
          // Handle object format
          const keys = Object.keys(g6Types);
          const values = Object.values(g6Types);
          const parsedTypes = values.filter(v => v && typeof v === 'string' && v.trim().length > 0);
          
          if (parsedTypes.length === 0) {
            const keyTypes = keys.filter(k => k && k.trim().length > 0);
            setG6ActionTypes(keyTypes);
          } else {
            setG6ActionTypes(parsedTypes);
          }
        } else {
          setG6ActionTypes([]);
          setG6Error("Unexpected format for G6 Action Types received from backend.");
        }
      } else {
        setG6Error(response.data.message || "Failed to retrieve G6 Action Types.");
      }
    } catch (error) {
      console.error("Error retrieving G6 Action Types:", error);
      
      if (error.response?.status === 401) {
        setG6Error("Your session has expired. Please sign in again.");
        localStorage.removeItem('token');
        navigate("/signin");
      } else {
        setG6Error("Failed to retrieve G6 Action Types. Please check console for details.");
      }
    } finally {
      setG6Loading(false);
    }
  };

  const handleG6TypeClick = (type) => {
    setSelectedG6Type(type);
    setG6Error(""); // Clear any previous errors
    console.log("Selected G6 Action Type:", type);
  };

  const handleTemplateClick = () => {
    if (selectedTemplate) {
      navigate("/template-viewer", { state: { selectedTemplate } });
    }
  };

  const handleRecordClick = () => {
    if (group && type && subtype && category) {
      navigate("/record-types", {
        state: { group, type, subtype, category }
      });
    }
  };

  const handleNextClick = () => {
    if (!selectedG6Type) {
      setG6Error("Please select an inspection type before proceeding.");
      return;
    }

    localStorage.setItem("selectedG6Type", selectedG6Type);
    console.log("Saved to localStorage:", {
      selectedTemplate,
      group,
      type,
      subtype,
      category,
      selectedG6Type
    });

    navigate("/inspection-result", {
      state: { selectedTemplate, group, type, subtype, category, selectedG6Type }
    });
  };

  if (!authenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-main-dark' : 'bg-gray-100'}`}>
        <div className="text-center p-6 rounded-xl bg-red-50 text-red-600 max-w-md">
          <div className="mb-4">
            <span className="text-2xl">❌</span>
          </div>
          <p className="text-lg font-medium">{errorMessage}</p>
          <button
            onClick={() => navigate('/signin')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col min-h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-main-dark text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      <SignInHeader
        user={user}
        isLoggedIn={authenticated}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
      />
      <Navigation
        activeTab="My Apps"
        setActiveTab={() => {}}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
      />

      <div className="flex flex-col items-center pt-[140px] pb-10 px-5 flex-grow">
        <div className="self-start ml-6 mt-4 mb-3 px-3 py-2">
          <button
            onClick={handleTemplateClick}
            className="bg-transparent border-none text-purple-700 font-medium cursor-pointer text-sm hover:text-purple-800"
            disabled={!selectedTemplate}
          >
            {selectedTemplate || "No Template Selected"} |
          </button>
          <button
            onClick={handleRecordClick}
            className="ml-1 bg-transparent border-none text-purple-700 font-medium cursor-pointer text-sm hover:text-purple-800"
            disabled={!group || !type || !subtype || !category}
          >
            {`${group}/${type}/${subtype}/${category}`}
          </button>
        </div>

        <div className="mb-6 text-center">
          <h1 className="text-2xl text-[#333] font-bold">
            Select the Inspection Type that will trigger the communication.
          </h1>
        </div>

        {g6Loading && (
          <div className="text-center text-gray-600 mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
            <p>Loading inspection types...</p>
          </div>
        )}

        {g6Error && (
          <div className="w-full max-w-md mb-4 p-3 rounded bg-red-50 border border-red-200">
            <div className="flex items-center justify-between">
              <p className="text-red-600 text-sm">{g6Error}</p>
              <button
                onClick={() => setG6Error("")}
                className="text-red-400 hover:text-red-600 text-lg"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {g6ActionTypes.length > 0 && (
          <div className="w-full max-w-[500px] rounded-lg shadow-lg overflow-hidden border border-gray-200">
            {/* Header with dropdown arrow */}
            <div className="flex justify-between items-center px-4 py-3 bg-[#DBDCED] text-[#41425A] font-semibold text-base">
              <span>Select the workflow task...</span>
              <div className="bg-[#41425A] text-[#DBDCED] px-3 py-1 rounded text-sm font-bold">
                
              </div>
            </div>

            {/* Dropdown options */}
            <ul className="list-none p-0 m-0 bg-[#41425A] text-[#DBDCED] max-h-64 overflow-y-auto">
              {g6ActionTypes.map((actionType, index) => (
                <li
                  key={index}
                  onClick={() => handleG6TypeClick(actionType)}
                  className={`px-4 py-3 cursor-pointer transition-colors duration-200 hover:bg-[#565873] border-b border-[#565873] last:border-b-0 font-medium
                    ${selectedG6Type === actionType ? 'bg-[#DBDCED] text-[#41425A]' : ''}
                  `}
                >
                  {actionType}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!g6Loading && !g6Error && g6ActionTypes.length === 0 && (
          <div className="text-center text-gray-600">
            <p className="mb-2">No inspection types available for the selected record type.</p>
            <button
              onClick={handleGetG6ActionTypes}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        )}

        <button
          className={`mt-6 px-5 py-2.5 rounded transition-colors duration-200 ${
            selectedG6Type && !g6Loading
              ? 'bg-[#41425a] text-[#dbdced] hover:bg-gray-900 cursor-pointer'
              : 'bg-gray-400 text-gray-600 cursor-not-allowed'
          }`}
          onClick={handleNextClick}
          disabled={!selectedG6Type || g6Loading}
        >
          {g6Loading ? 'Loading...' : 'Next'}
          {!g6Loading && (
            <svg className="w-4 h-4 inline ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default InspectionType;